import * as Sentry from '@sentry/browser'

type StreamOutcome = 'ended' | 'aborted' | 'refused' | 'failed'

const FIRST_RECONNECT_DELAY = 1000
const MAX_RECONNECT_DELAY = 30 * 1000
const MAX_REFUSALS = 3

export default () => {
  const { userInfo } = storeToRefs(useUserStore())
  const callStore = useCallStore()
  const { getEffectiveAuthToken } = useEffectiveAuthToken()

  let abortController: AbortController | null = null
  let streamStartedAt = 0
  let refusals = 0
  let reconnectDelay = FIRST_RECONNECT_DELAY
  let interruptDelay: (() => void) | null = null
  let shouldStayConnected = false
  let supervisorRunning = false

  const handleEvent = (eventType: string, eventData: string) => {
    if (!eventData) { return }
    try {
      let payload = JSON.parse(eventData)
      if (typeof payload === 'string') {
        payload = JSON.parse(payload)
      }
      // eslint-disable-next-line no-console
      console.log(`Received event of type ${eventType} with payload:`, payload)
      if (eventType === 'call_update') {
        const callStore = useCallStore()
        callStore.processEventStream(payload)
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  const createFrameParser = () => {
    let eventType = 'message'
    let eventData = ''
    return (line: string) => {
      if (line.startsWith('event:')) {
        eventType = line.slice(6).trim()
      } else if (line.startsWith('data:')) {
        eventData += (eventData ? '\n' : '') + line.slice(5).trim()
      } else if (line.trim() === '') {
        handleEvent(eventType, eventData)
        eventType = 'message'
        eventData = ''
      }
    }
  }

  const readStream = async (body: ReadableStream<Uint8Array>) => {
    const reader = body.getReader()
    const decoder = new TextDecoder()
    const parseLine = createFrameParser()
    let buffer = ''
    while (true) {
      const { done, value } = await reader.read()
      if (done) { return }
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      // The last piece is held back: without a trailing newline it is an incomplete line.
      buffer = lines.pop() ?? ''
      lines.forEach(parseLine)
    }
  }

  const openStream = async (loginId: string, authToken: string): Promise<StreamOutcome> => {
    abortController = new AbortController()
    try {
      // We need to use fetch() because it gave us access to the raw response stream.
      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}api/events/?login_id=${loginId}`, {
        signal: abortController.signal,
        headers: {
          Authorization: authToken,
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache'
        }
      })
      if (response.status >= 400 && response.status < 500) {
        Sentry.captureException(new Error(`Event stream refused the connection: ${response.status}`))
        return 'refused'
      }
      if (!response.ok || !response.body) {
        throw new Error(`Failed to connect to event stream: ${response.status}`)
      }
      callStore.isEventStreamConnected = true
      streamStartedAt = Date.now()
      await readStream(response.body)
      return 'ended'
    } catch (error) {
      if ((error as Error).name === 'AbortError') { return 'aborted' }
      Sentry.captureException(error)
      return 'failed'
    } finally {
      callStore.isEventStreamConnected = false
    }
  }

  const waitForLoginId = (): Promise<string | null> => {
    const id = userInfo.value?.id
    if (id) { return Promise.resolve(id) }
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        unwatch()
        resolve(null)
      }, 5000)
      const unwatch = watch(
        () => userInfo.value?.id,
        (newId) => {
          if (newId) {
            clearTimeout(timeout)
            unwatch()
            resolve(newId)
          }
        }
      )
    })
  }

  // Also reads the flag, because a connect() may have re-armed it during this attempt.
  const keepTrying = async () => {
    // Per attempt: a leftover stamp would wipe this attempt's refusal and its backoff.
    streamStartedAt = 0
    const loginId = await waitForLoginId()
    const authToken = await getEffectiveAuthToken()
    let outcome: StreamOutcome = 'failed'
    if (loginId && authToken) { outcome = await openStream(loginId, authToken) }
    if (outcome === 'refused') { refusals++ }
    return shouldStayConnected && refusals < MAX_REFUSALS
  }

  const waitBeforeReconnect = (): Promise<void> => {
    const lasted = streamStartedAt ? Date.now() - streamStartedAt : 0
    if (lasted) { refusals = 0 }
    // A connection that outlived the longest backoff was healthy: its end is a blip.
    if (lasted > MAX_RECONNECT_DELAY) { reconnectDelay = FIRST_RECONNECT_DELAY }
    const delay = reconnectDelay
    reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY)
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        interruptDelay = null
        resolve()
      }, delay)
      interruptDelay = () => {
        clearTimeout(timer)
        interruptDelay = null
        resolve()
      }
    })
  }

  const connect = async () => {
    shouldStayConnected = true
    interruptDelay?.()
    if (supervisorRunning) { return }
    supervisorRunning = true
    refusals = 0
    reconnectDelay = FIRST_RECONNECT_DELAY
    try {
      while (shouldStayConnected) {
        shouldStayConnected = await keepTrying()
        if (shouldStayConnected) { await waitBeforeReconnect() }
      }
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      supervisorRunning = false
    }
  }

  const disconnect = () => {
    shouldStayConnected = false
    interruptDelay?.()
    abortController?.abort()
    abortController = null
  }

  return { connect, disconnect }
}
